import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import type { Notification } from "../../backend/api/notifications";
import {
  useNotificationCount,
  useNotifications,
  useReadNotification,
  useReadAllNotifications,
} from "../../backend/queries/notifications";
import { useOnlineStatus } from "../../hooks/use-online-status";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { DataView, EmptyState, ErrorState, StatusBadge } from "../patterns";

export function NotificationInbox({ userId }: { userId: string }) {
  const [open, setOpen] = useState(false);
  const [cursors, setCursors] = useState<string[]>([]);
  const [actionError, setActionError] = useState("");
  const count = useNotificationCount(userId);
  const page = useNotifications(userId, open, cursors[cursors.length - 1]);
  const read = useReadNotification(userId);
  const readAll = useReadAllNotifications(userId);
  const online = useOnlineStatus();
  const navigate = useNavigate();
  const busy = read.isPending || readAll.isPending;
  const unread = count.data ?? 0;

  async function openNotification(item: Notification) {
    setActionError("");
    try {
      if (!item.read_at) await read.mutateAsync(item.id);
      setOpen(false);
      navigate(item.href);
    } catch {
      setActionError(
        "Could not open this notification. Try again; your access may have changed.",
      );
    }
  }

  async function markRead(item: Notification) {
    setActionError("");
    try {
      await read.mutateAsync(item.id);
    } catch {
      setActionError("Could not mark the notification as read. Try again.");
    }
  }

  async function markAllRead() {
    if (!page.data) return;
    setActionError("");
    try {
      await readAll.mutateAsync(page.data.as_of);
    } catch {
      setActionError("Could not mark notifications as read. Try again.");
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (value) {
          setCursors([]);
          setActionError("");
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          className="min-h-touch min-w-touch gap-2"
          aria-label={
            count.isError
              ? "Notifications, unable to refresh"
              : `Notifications, ${unread} unread`
          }
        >
          <Bell className="h-5 w-5" aria-hidden="true" />
          <span className="hidden sm:inline">Notifications</span>
          {unread > 0 && (
            <StatusBadge tone="info" marker={false}>
              {unread > 99 ? "99+" : unread}
            </StatusBadge>
          )}
          {count.isError && (
            <span aria-hidden="true" className="text-tone-warning-foreground">
              !
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl shadow-e2 duration-base">
        <DialogHeader>
          <DialogTitle>Notifications</DialogTitle>
          <DialogDescription>
            Task assignments and updates for you.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p role="status" className="text-sm text-muted-foreground">
            {count.isError ? "Unread count unavailable" : `${unread} unread`}
          </p>
          <Button
            variant="outline"
            size="sm"
            disabled={!online || busy || !page.data || unread === 0}
            onClick={() => void markAllRead()}
          >
            Mark all as read
          </Button>
        </div>
        {!online && (
          <p role="status" className="text-sm text-muted-foreground">
            You’re offline. Reconnect to update notifications.
          </p>
        )}
        {actionError && (
          <ErrorState
            title="Notification update failed"
            description={actionError}
          />
        )}
        <DataView<Notification>
          caption="Notifications"
          rows={page.data?.items ?? []}
          getRowKey={(item) => item.id}
          columns={[
            {
              id: "notification",
              header: "Recent updates",
              primary: true,
              cell: (item) => (
                <div className="flex min-w-0 flex-col items-start gap-2">
                  <button
                    type="button"
                    className="min-h-touch w-full text-left hover:underline disabled:opacity-50"
                    disabled={busy || !online}
                    onClick={() => void openNotification(item)}
                  >
                    <span className="block font-medium">{item.title}</span>
                    <span className="block break-words text-sm text-muted-foreground">
                      {item.body}
                    </span>
                  </button>
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge tone={item.read_at ? "neutral" : "info"}>
                      {item.read_at ? "Read" : "Unread"}
                    </StatusBadge>
                    <time
                      className="text-xs text-muted-foreground"
                      dateTime={item.created_at}
                    >
                      {new Date(item.created_at).toLocaleString()}
                    </time>
                  </div>
                </div>
              ),
            },
          ]}
          rowActions={(item) =>
            !item.read_at && (
              <Button
                size="icon"
                variant="ghost"
                className="min-h-touch min-w-touch"
                aria-label={`Mark ${item.body} as read`}
                disabled={busy || !online}
                onClick={() => void markRead(item)}
              >
                <Check className="h-4 w-4" aria-hidden="true" />
              </Button>
            )
          }
          status={page.isPending ? "loading" : page.isError ? "error" : "ready"}
          errorTitle="Could not load notifications"
          onRetry={() => {
            void page.refetch();
            void count.refetch();
          }}
          empty={
            <EmptyState
              title="No notifications yet"
              description="When a task is assigned to you, it will appear here."
            />
          }
        />
        <nav
          aria-label="Notification pages"
          className="flex items-center justify-between gap-2"
        >
          <Button
            variant="outline"
            disabled={cursors.length === 0 || busy || page.isFetching}
            onClick={() => setCursors((values) => values.slice(0, -1))}
          >
            Newer
          </Button>
          <span className="text-sm text-muted-foreground" role="status">
            Page {cursors.length + 1}
          </span>
          <Button
            variant="outline"
            disabled={!page.data?.next_cursor || busy || page.isFetching}
            onClick={() => {
              const next = page.data?.next_cursor;
              if (next) setCursors((values) => [...values, next]);
            }}
          >
            Older
          </Button>
        </nav>
      </DialogContent>
    </Dialog>
  );
}
